/**
 * Hook: useOrganizations
 *
 * Loads the current user's org memberships and orgs on mount.
 * Exposes getPrimaryOrg() and getOrgRole() helpers.
 *
 * Doctrine: ROLE_ARCHITECTURE.md
 * - This hook is read-only. It does not write to any table.
 * - Org data has zero effect on competitive engine outputs.
 */

import { useState, useEffect, useCallback } from 'react';
import { createLogger } from '@/lib/logger';
import { useAuth } from '@/contexts/AuthContext';
import {
  fetchMyMemberships,
  fetchOrganizations,
  getPrimaryOrg,
  getOrgRole,
  isOrgAdmin,
} from '@/lib/organizations';
import type {
  Organization,
  OrganizationMembership,
  OrgType,
  OrgRole,
} from '@/types/database';

const log = createLogger('USEORGANIZATIONS');

export function useOrganizations() {
  const { profile } = useAuth();
  const [memberships, setMemberships] = useState<OrganizationMembership[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!profile?.id) {
      setMemberships([]);
      setOrganizations([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const myMemberships = await fetchMyMemberships(profile.id);
      setMemberships(myMemberships);

      const orgIds = [...new Set(myMemberships.map((m) => m.org_id))];
      const orgs = await fetchOrganizations(orgIds);
      setOrganizations(orgs);
    } catch (e) {
      log.warn('[useOrganizations] load failed', e);
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    memberships,
    organizations,
    loading,
    reload: load,
    getPrimaryOrg: (orgType: OrgType) => getPrimaryOrg(memberships, orgType, organizations),
    getOrgRole: (orgId: string): OrgRole | null => getOrgRole(memberships, orgId),
    isOrgAdmin: (orgId: string): boolean => isOrgAdmin(memberships, orgId),
  };
}
